import styled from "@emotion/styled";
import { useAtom } from "jotai";
import {
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type MouseEventHandler,
  type PropsWithChildren,
} from "react";
import { useLocation } from "react-router";

import {
  FONT_WEIGHT_SEMI_BOLD,
  Icon,
  IconButton,
  NAV_BAR_HEIGHT,
  Popper,
  zIndexes,
} from "@skilitics/designer-ui-components";

import { collapseThumbnailNavigationBar } from "../application/applicationSettingsAtoms";
import { PickingShield, useNodePickingActive } from "../composable/picking";
import { InterludeEventTypes, useEventSubscribe } from "../events";
import { AddFrameButton } from "./AddFrameButton";
import { AddOverlayButton } from "./AddOverlayButton";
import { FrameNavigationThumbnail } from "./FrameNavigationThumbnail";
import { InterludeNavigationThumbnail } from "./InterludeNavigationThumbnail";
import { NavigationThumbnail } from "./NavigationThumbnail";
import type { InterludeThumbnailNavItemSet, ThumbnailNavItemSet } from "./types";

type Props = ComponentProps<"div"> & {
  sets: ThumbnailNavItemSet[];
  overlay?: boolean;
  hidePanels?: boolean;
  selectedId?: number;
  // A composable canvas's layers/components/library panel overlays the start of the bar; the parent
  // rendering that canvas passes its width here so the thumbnails stay centred in the visible space.
  reservedStartWidth?: string;
};

export const ThumbnailNavigationBar = ({
  sets,
  hidePanels,
  selectedId,
  reservedStartWidth = "0px",
  ...divProps
}: PropsWithChildren<Props>) => {
  const [collapsed, setCollapsed] = useAtom(collapseThumbnailNavigationBar);

  const location = useLocation();
  const nodePickingActive = useNodePickingActive();

  const interludeSelected = location.pathname.includes("interludes");
  const hideInterludeThumbnails = !interludeSelected && location.pathname.includes("editor");

  const handleCollapseClick: MouseEventHandler = (e) => {
    e.stopPropagation();
    setCollapsed(!collapsed);
  };

  const [addedInterludeId, setAddedInterludeId] = useState<number | undefined>();

  useEventSubscribe(InterludeEventTypes.INTERLUDE_ADDED, ({ interludeOrchestratorId, interludeId }) => {
    const interludeSets = sets.filter(({ type }) => type === "interludes") as InterludeThumbnailNavItemSet[];

    if (interludeSets.find(({ orchestratorId }) => interludeOrchestratorId === orchestratorId)) {
      setAddedInterludeId(interludeId);
    }
  });

  const collapseStyle: CSSProperties = {
    opacity: nodePickingActive ? 0 : 1,
    pointerEvents: nodePickingActive ? "none" : "auto",
    transition: "opacity 150ms ease-in-out",
  };

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = containerRef.current;

    if (element && !anchorEl) {
      setAnchorEl(containerRef.current);
    }
  }, [anchorEl]);

  return (
    <Container
      ref={containerRef}
      {...divProps}
      $collapsed={collapsed}
      onClick={(event) => event.stopPropagation()}
      style={{
        ...divProps.style,
        ...(hidePanels ? { display: "none" } : {}),
      }}
    >
      <PickingShield />
      <Popper
        open={!!anchorEl}
        placement="top-end"
        modifiers={[{ name: "offset", options: { offset: collapsed ? [-14, 2] : [-14, -14] } }]}
        anchorEl={anchorEl}
        style={{
          zIndex: zIndexes.triggersOverlay,
        }}
      >
        <ToggleCollapseButton
          onClick={handleCollapseClick}
          style={collapseStyle}
          data-test="toggle-collapse-thumbnail-nav"
        >
          <Icon icon={collapsed ? "angles-up" : "angles-down"} />
        </ToggleCollapseButton>
      </Popper>

      <CollapseContainer $reservedStartWidth={reservedStartWidth}>
        {sets
          .filter(({ type }) => !(hideInterludeThumbnails && type === "interludes"))
          .map(({ type, heading, noItemsHeading, addButtonTooltip, items }) => (
            <SetContainer key={heading} id={`thumbnails-container-${type}`}>
              <Separator />
              <Heading>{heading}</Heading>

              {items.length === 0 && <ContentEmpty>{noItemsHeading}</ContentEmpty>}

              {items.length > 0 && (
                <Content>
                  {type === "activities"
                    ? items.map((item) => (
                        <NavigationThumbnail key={item.node.id} {...item} selected={item.node.id === selectedId} />
                      ))
                    : type === "interludes"
                      ? items.map((item) =>
                          "node" in item ? (
                            <InterludeNavigationThumbnail
                              key={`${item.node.id}-${addedInterludeId}`}
                              {...item}
                              selected={item.node.id === selectedId}
                              menu={item.menu && item.node.id !== selectedId}
                              linked={item.linked}
                              added={item.node.id === addedInterludeId}
                              onAddedComplete={() => setAddedInterludeId(undefined)}
                            />
                          ) : (
                            <AddOverlayButton
                              key={item.orchestratorId}
                              interludeOrchestratorId={item.orchestratorId}
                              browseTarget={item.browseTarget}
                              templateCriteria={item.templateCriteria}
                              tooltip={addButtonTooltip}
                            />
                          ),
                        )
                      : items.map((item) =>
                          "node" in item ? (
                            <FrameNavigationThumbnail
                              key={item.node.id}
                              {...item}
                              selected={item.node.id === selectedId}
                              menu={item.menu && item.node.id !== selectedId}
                            />
                          ) : (
                            <AddFrameButton
                              key={item.navGroupId}
                              navGroupId={item.navGroupId}
                              browseTarget={item.browseTarget}
                              templateCriteria={item.templateCriteria}
                              tooltip={addButtonTooltip}
                            />
                          ),
                        )}
                </Content>
              )}
            </SetContainer>
          ))}
      </CollapseContainer>
    </Container>
  );
};

const CollapseContainer = styled.div<{ $reservedStartWidth: string }>`
  width: ${({ $reservedStartWidth }) => `calc(100% - ${$reservedStartWidth})`};
  margin-inline-start: ${({ $reservedStartWidth }) => $reservedStartWidth};
  height: 100%;

  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;

  display: flex;
  align-items: center;
  /* centre while it fits, but fall back to start-aligned once the thumbnails
     overflow so none get clipped out of reach */
  justify-content: safe center;
`;

const SetContainer = styled.div`
  display: contents;
`;

const ToggleCollapseButton = styled(IconButton)`
  background-color: ${({ theme }) => (theme.type === "light" ? theme.surfaceBack_1 : theme.surfaceBack_2)};
  color: ${({ theme }) => theme.generalTextIcon};

  transition: color 200ms ease-in-out;

  &:hover {
    background-color: ${({ theme }) => (theme.type === "light" ? theme.surfaceBack_1 : theme.surfaceBack_2)};
    color: ${({ theme }) => theme.primary};
  }
`;

const Container = styled.div<{ $collapsed: boolean }>`
  position: relative;
  flex: 0 0 ${NAV_BAR_HEIGHT}px;

  height: ${NAV_BAR_HEIGHT}px;
  width: 100%;

  background-color: ${({ theme }) => (theme.type === "light" ? theme.surfaceBack_1 : theme.surfaceBack_2)};
  color: ${({ theme }) => theme.generalTextIcon};

  overflow-x: clip;
  overflow-y: visible;
  z-index: ${zIndexes.composableNavigationBar};

  ${({ $collapsed }) =>
    $collapsed &&
    `
    flex-basis: 0;
    height: 0;
`}
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  gap: 10px;

  height: 100%;
`;

const ContentEmpty = styled(Content)`
  min-width: 80px;
  color: ${({ theme }) => theme.generalTextIconVariant};

  user-select: none;
`;

const Heading = styled.div`
  font-size: ${({ theme }) => theme.fontSize};
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  text-transform: uppercase;
  line-height: 1em;
  padding-block: 12px;
  flex: none;

  overflow: hidden;
  writing-mode: vertical-lr;

  rotate: 180deg;

  user-select: none;
`;

const Separator = styled.div`
  height: 100%;

  border-inline-start: 2px solid ${({ theme }) => theme.moduleMap.background};
  margin-inline-start: 20px;
`;
